import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import {
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronRight,
  ListFilter,
  Search,
  X,
} from "lucide-react";
import {
  differenceInDays,
  intervalToDuration,
} from "date-fns";
import { cn } from "@/modern/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/modern/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";
import {
  compareUsers,
  groupKeyFor,
  ROLE_ORDER,
  useUsersContext,
  type UserListStats,
  type UserSort,
} from "../context/UsersContext";
import { UserAvatar } from "@/modern/components/user-avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modern/components/ui/tooltip";
import {
  USERS_EMPTY_CLS,
  USERS_HELPER_CLS,
  USERS_INPUT_CLS,
  USERS_SUBTITLE_CLS,
} from "../constants/typography";
import { getRoleIcon } from "../constants/roles";

const EASE = "ease-[cubic-bezier(0.23,1,0.32,1)]";

const SORT_OPTIONS: {
  id: UserSort;
  label: string;
  hint: string;
  faIcon: string;
}[] = [
  { id: "name", label: "Name", hint: "A → Z", faIcon: "fa-light fa-font" },
  { id: "age", label: "Age", hint: "Oldest first", faIcon: "fa-light fa-calendar" },
  { id: "points", label: "Points", hint: "Highest first", faIcon: "fa-light fa-award" },
  { id: "tasks", label: "Tasks", hint: "Most scheduled", faIcon: "fa-light fa-swap-arrows" },
];

const menuClass = cn(
  "min-w-[13.5rem] rounded-xl border border-border/80 bg-popover p-1.5",
  "text-popover-foreground shadow-lg",
);

function MenuHeading({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 pt-1 pb-1.5">
      <p className="text-sm font-medium tracking-tight text-muted-foreground/75">
        {children}
      </p>
    </div>
  );
}

const ToolbarChip = forwardRef<
  HTMLButtonElement,
  ComponentProps<"button"> & { active?: boolean }
>(function ToolbarChip({ active, className, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5",
        "text-base font-medium tracking-tight",
        "transition-colors duration-150",
        EASE,
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "active:scale-[0.98]",
        active
          ? "bg-primary/12 text-primary hover:bg-primary/16"
          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
        "data-[state=open]:bg-muted/70 data-[state=open]:text-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});
ToolbarChip.displayName = "ToolbarChip";

function RoleFilterSelect() {
  const { availableRoles, roleFilters, toggleRoleFilter, clearRoleFilters } =
    useUsersContext();

  const hasFilters = roleFilters.length > 0;
  const label = !hasFilters
    ? "Role"
    : roleFilters.length === 1
      ? roleFilters[0]
      : `${roleFilters.length} roles`;
  const SelectedRoleIcon =
    hasFilters && roleFilters.length === 1 ? getRoleIcon(roleFilters[0]) : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ToolbarChip active={hasFilters} className="max-w-[8rem] shrink-0" title="Filter by role">
          {SelectedRoleIcon ? (
            <SelectedRoleIcon
              aria-hidden
              className="size-3.5 shrink-0"
              strokeWidth={1.75}
            />
          ) : (
            <ListFilter aria-hidden className="size-3.5 shrink-0 opacity-70" strokeWidth={1.75} />
          )}
          <span className="min-w-0 truncate">{label}</span>
          {hasFilters && roleFilters.length > 1 ? (
            <span className="tabular-nums opacity-80">{roleFilters.length}</span>
          ) : null}
          <ChevronDown className="size-3.5 shrink-0 opacity-50" />
        </ToolbarChip>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className={menuClass}>
        <MenuHeading>Filter by role</MenuHeading>
        <div className="space-y-0.5">
          {availableRoles.map((role) => {
            const on = roleFilters.includes(role);
            const RoleIcon = getRoleIcon(role);
            return (
              <DropdownMenuItem
                key={role}
                onSelect={(e) => {
                  e.preventDefault();
                  toggleRoleFilter(role);
                }}
                className={cn(
                  "cursor-pointer gap-2.5 rounded-lg px-2.5 py-2",
                  "focus:bg-muted/50",
                  on && "bg-primary/10 focus:bg-primary/12",
                )}
              >
                <RoleIcon
                  aria-hidden
                  className="size-3.5 shrink-0 text-muted-foreground"
                  strokeWidth={1.75}
                />
                <span className="min-w-0 flex-1 truncate text-base font-normal tracking-tight">
                  {role}
                </span>
                {on ? (
                  <Check className="size-3.5 shrink-0 text-primary" strokeWidth={2.5} />
                ) : (
                  <span className="size-3.5 shrink-0" aria-hidden />
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
        {hasFilters ? (
          <>
            <DropdownMenuSeparator className="my-1.5 bg-border/70" />
            <button
              type="button"
              onClick={clearRoleFilters}
              className={cn(
                "flex w-full items-center justify-center gap-1.5 rounded-lg px-2.5 py-2",
                USERS_HELPER_CLS,
                "hover:bg-muted/40 hover:text-foreground transition-colors",
              )}
            >
              Clear filters
            </button>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SortSelect() {
  const { sortBy, setSortBy } = useUsersContext();
  const current = SORT_OPTIONS.find((o) => o.id === sortBy) ?? SORT_OPTIONS[0];
  const active = sortBy !== "name";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ToolbarChip active={active} className="shrink-0" title="Sort by">
          <ArrowUpDown className="size-3.5 shrink-0 opacity-70" />
          <span>{current.label}</span>
          <ChevronDown className="size-3.5 shrink-0 opacity-50" />
        </ToolbarChip>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className={menuClass}>
        <MenuHeading>Sort by</MenuHeading>
        <div className="space-y-0.5">
          {SORT_OPTIONS.map((opt) => {
            const on = sortBy === opt.id;
            return (
              <DropdownMenuItem
                key={opt.id}
                onSelect={() => setSortBy(opt.id)}
                className={cn(
                  "cursor-pointer gap-2.5 rounded-lg px-2.5 py-2",
                  "focus:bg-muted/50",
                  on && "bg-primary/10 focus:bg-primary/12",
                )}
              >
                <span
                  className={cn(
                    "flex size-7 shrink-0 items-center justify-center rounded-md",
                    on
                      ? "bg-primary/15 text-primary"
                      : "bg-muted/60 text-muted-foreground",
                  )}
                >
                  <i className={cn(opt.faIcon, "text-base")} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-base font-normal tracking-tight text-foreground">
                    {opt.label}
                  </span>
                  <span className={cn("mt-0.5 block leading-tight", USERS_SUBTITLE_CLS)}>
                    {opt.hint}
                  </span>
                </span>
                {on ? (
                  <Check className="size-3.5 shrink-0 text-primary" strokeWidth={2.5} />
                ) : (
                  <span className="size-3.5 shrink-0" aria-hidden />
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DirectoryToolbar() {
  const { query, setQuery } = useUsersContext();

  return (
    <div
      className={cn(
        "flex items-center gap-1 rounded-xl",
        "bg-muted/40 ring-1 ring-inset ring-border/50",
        "pl-3 pr-1.5 py-1.5",
        "transition-[box-shadow,background-color] duration-150",
        EASE,
        "focus-within:bg-muted/55 focus-within:ring-primary/35",
      )}
    >
      <Search
        className="size-4 shrink-0 text-muted-foreground/70 pointer-events-none"
        strokeWidth={1.75}
      />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search users…"
        aria-label="Search users"
        className={cn(
          "min-w-0 flex-1 bg-transparent border-0 px-2.5 py-1.5",
          USERS_INPUT_CLS,
          "font-normal text-foreground placeholder:text-muted-foreground/60",
          "focus:outline-none focus:ring-0",
        )}
      />
      {query ? (
        <button
          type="button"
          onClick={() => setQuery("")}
          className={cn(
            "size-8 shrink-0 rounded-lg flex items-center justify-center",
            "text-muted-foreground hover:text-foreground hover:bg-background/50",
            "transition-colors duration-150",
          )}
          aria-label="Clear search"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
      <span aria-hidden className="mx-0.5 h-4 w-px shrink-0 bg-border/70" />
      <RoleFilterSelect />
      <span aria-hidden className="h-4 w-px shrink-0 bg-border/70" />
      <SortSelect />
    </div>
  );
}

function RoleGroupHeader({
  role,
  count,
  open,
  onAltClick,
}: {
  role: string;
  count: number;
  open: boolean;
  onAltClick?: () => void;
}) {
  const RoleIcon = getRoleIcon(role);
  return (
    <CollapsibleTrigger
      className={cn(
        "group/hdr w-full text-left",
        "px-2 py-1",
        "focus-visible:outline-none",
      )}
      onPointerDown={(e) => {
        if (!e.altKey || !onAltClick) return;
        e.preventDefault();
        onAltClick();
      }}
      title="Option/Alt+click to close all groups"
    >
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg",
          "bg-muted/60 border border-border/60",
          "transition-colors duration-150",
          EASE,
          "hover:bg-muted/80 hover:border-border/80",
        )}
      >
        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-150",
            EASE,
            "group-hover/hdr:text-muted-foreground",
            open && "rotate-90 text-muted-foreground",
          )}
          strokeWidth={2}
        />
        <RoleIcon
          aria-hidden
          className="size-3.5 shrink-0 text-muted-foreground/80 group-hover/hdr:text-foreground/70"
          strokeWidth={1.75}
        />
        <span className="min-w-0 flex-1 truncate text-left text-sm font-medium tracking-tight text-foreground/70 group-hover/hdr:text-foreground/90">
          {role}
        </span>
        <span
          className={cn(
            "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5",
            "text-xs font-medium tabular-nums text-muted-foreground",
            "bg-background/70 border border-border/50",
          )}
        >
          {count}
        </span>
      </div>
    </CollapsibleTrigger>
  );
}

function MetricChip({
  icon,
  value,
  label,
  tipTitle,
  tipBody,
}: {
  icon: string;
  value: string;
  label: string;
  tipTitle: string;
  tipBody: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 leading-none",
            "text-sm text-muted-foreground",
            "cursor-default",
          )}
        >
          <i
            className={cn("fa-light text-sm leading-none opacity-70", icon)}
            aria-hidden
          />
          <span className="tabular-nums font-medium leading-none text-foreground/85">
            {value}
          </span>
          <span className="sr-only">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="bg-popover text-popover-foreground border border-border shadow-md"
      >
        <p className="font-medium">{tipTitle}</p>
        <p className="text-muted-foreground">{tipBody}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function roleSortKey(role: string) {
  const i = ROLE_ORDER.indexOf(role);
  return i === -1 ? 1000 + role.charCodeAt(0) : i;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function groupByRole(
  list: any[],
  sortBy: UserSort,
  statsMap?: Map<string | number, UserListStats>,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const map = new Map<string, any[]>();
  for (const u of list) {
    const key = groupKeyFor(u);
    const bucket = map.get(key);
    if (bucket) bucket.push(u);
    else map.set(key, [u]);
  }
  return [...map.entries()]
    .map(([role, members]) => ({
      role,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      members: [...members].sort((a: any, b: any) =>
        compareUsers(a, b, sortBy, statsMap),
      ),
    }))
    .sort((a, b) => {
      const d = roleSortKey(a.role) - roleSortKey(b.role);
      return d !== 0 ? d : a.role.localeCompare(b.role);
    });
}

function formatUserAging(user: any): { short: string; tipBody: string } | null {
  try {
    const createdRaw = user.createdAt;
    const createdAt = createdRaw ? new Date(createdRaw) : null;
    const hasCreated =
      createdAt instanceof Date && !Number.isNaN(createdAt.getTime());

    const totalDays = hasCreated
      ? Math.max(0, differenceInDays(new Date(), createdAt))
      : Math.max(0, Math.round(Number(user.aging)));

    if (!Number.isFinite(totalDays) || totalDays <= 0) return null;

    const start = hasCreated
      ? createdAt
      : new Date(Date.now() - totalDays * 24 * 60 * 60 * 1000);
    const end = new Date();
    const duration = intervalToDuration({ start, end });
    const years = duration.years ?? 0;
    const months = duration.months ?? 0;
    const days = duration.days ?? 0;

    let short: string;
    if (totalDays >= 360 || years >= 1) {
      short = months > 0 ? `${years}y ${months}mo` : `${Math.max(years, 1)}y`;
    } else if (months >= 1) {
      short = `${months}mo`;
    } else {
      short = `${Math.max(days, totalDays, 1)}d`;
    }

    const memberSinceDate = start.toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return {
      short,
      tipBody: `Member since ${memberSinceDate}`,
    };
  } catch {
    return null;
  }
}

function UserCard({
  user,
  active,
  onSelect,
  resourceCount,
  pointsTotal,
  scheduledHours,
  taskCount,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any;
  active: boolean;
  onSelect: () => void;
  resourceCount: number;
  pointsTotal: number;
  scheduledHours: number;
  taskCount: number;
}) {
  const hoursLabel =
    scheduledHours % 1 === 0
      ? `${scheduledHours}h`
      : `${scheduledHours.toFixed(1)}h`;
  const aging = formatUserAging(user);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? "true" : undefined}
      className={cn(
        "group/card w-full text-left rounded-xl",
        "px-3 py-3.5",
        "transition-[background-color,transform] duration-150",
        EASE,
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "active:scale-[0.995]",
        active
          ? "bg-muted/60"
          : "bg-transparent hover:bg-muted/35",
      )}
    >
      <div className="flex items-start gap-3">
        <UserAvatar
          user={user}
          size="default"
          className="shrink-0"
          avatarClassName={cn(
            "h-9 w-9 text-base rounded-full",
            "ring-2 ring-background",
            active && "ring-primary/20",
          )}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <p
                className={cn(
                  "m-0 truncate text-base font-medium tracking-tight leading-none",
                  active ? "text-foreground" : "text-foreground/90",
                )}
              >
                {user.name}
              </p>
              {user.email ? (
                <p className={cn("m-0 truncate leading-none", USERS_SUBTITLE_CLS)}>
                  {user.email}
                </p>
              ) : (
                <p className={cn("m-0 truncate leading-none", USERS_HELPER_CLS)}>
                  No email
                </p>
              )}
            </div>

            <Tooltip>
              <TooltipTrigger asChild>
                <div className="shrink-0 text-right space-y-1">
                  <p className="m-0 text-base font-medium tabular-nums leading-none tracking-tight text-foreground">
                    {hoursLabel}
                  </p>
                  <p className="m-0 text-sm text-muted-foreground leading-none">
                    Scheduled
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className="bg-popover text-popover-foreground border border-border shadow-md"
              >
                <p className="font-medium">Scheduled hours</p>
                <p className="text-muted-foreground">{hoursLabel} on the board</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {aging ? (
              <MetricChip
                icon="fa-clock"
                value={aging.short}
                label="membership age"
                tipTitle="Member since"
                tipBody={aging.tipBody}
              />
            ) : null}
            <MetricChip
              icon="fa-screwdriver-wrench"
              value={String(resourceCount)}
              label="resources"
              tipTitle="Assigned resources"
              tipBody={`${resourceCount} on this board`}
            />
            <MetricChip
              icon="fa-award"
              value={pointsTotal.toLocaleString()}
              label="points"
              tipTitle="Points awarded"
              tipBody={`${pointsTotal.toLocaleString()} total`}
            />
            <MetricChip
              icon="fa-swap-arrows"
              value={String(taskCount)}
              label="tasks"
              tipTitle="Tasks scheduled"
              tipBody={`${taskCount} scheduled on the board`}
            />
          </div>
        </div>
      </div>
    </button>
  );
}

export function UsersSidebar() {
  const {
    filteredUsers,
    selectedUser,
    selectUser,
    query,
    roleFilters,
    sortBy,
    userStatsMap,
  } = useUsersContext();

  const groups = useMemo(
    () => groupByRole(filteredUsers, sortBy, userStatsMap),
    [filteredUsers, sortBy, userStatsMap],
  );
  const roleKeys = useMemo(() => groups.map((g) => g.role), [groups]);

  const [openRoles, setOpenRoles] = useState<Set<string>>(() => new Set());
  const seeded = useRef(false);
  const prevSelectedId = useRef(selectedUser?.id);
  const roleKeysKey = roleKeys.join("\0");
  const roleFiltersKey = roleFilters.join("\0");

  useEffect(() => {
    setOpenRoles((prev) => {
      if (!seeded.current && roleKeys.length > 0) {
        seeded.current = true;
        return new Set(roleKeys);
      }
      const next = new Set([...prev].filter((k) => roleKeys.includes(k)));
      if (next.size === prev.size && [...next].every((k) => prev.has(k))) return prev;
      return next;
    });
  }, [roleKeys, roleKeysKey]);

  useEffect(() => {
    if (prevSelectedId.current === selectedUser?.id) return;
    prevSelectedId.current = selectedUser?.id;
    if (!selectedUser) return;
    const key = groupKeyFor(selectedUser);
    setOpenRoles((prev) => {
      if (prev.has(key) || !roleKeys.includes(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, [selectedUser, roleKeys]);

  useEffect(() => {
    if ((!query.trim() && roleFilters.length === 0) || roleKeys.length === 0) return;
    setOpenRoles(new Set(roleKeys));
  }, [query, roleFiltersKey, roleFilters.length, roleKeysKey, roleKeys]);

  const closeAllGroups = () => setOpenRoles(new Set());

  return (
    <aside className="col-span-12 lg:col-span-4 xl:col-span-3 min-w-0 min-h-0 flex flex-col overflow-hidden bg-background border-r border-border">
      <div className="shrink-0 px-3 pt-3 pb-2">
        <DirectoryToolbar />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-thin">
        {groups.length === 0 ? (
          <p className={cn("px-6 py-16 text-center", USERS_EMPTY_CLS)}>
            No users match this search.
          </p>
        ) : (
          <div className="flex flex-col gap-2 pb-4 pt-1">
            {groups.map((g) => {
              const open = openRoles.has(g.role);
              return (
                <Collapsible
                  key={g.role}
                  open={open}
                  onOpenChange={(next) => {
                    setOpenRoles((prev) => {
                      const nextSet = new Set(prev);
                      if (next) nextSet.add(g.role);
                      else nextSet.delete(g.role);
                      return nextSet;
                    });
                  }}
                  className="min-w-0"
                >
                  <RoleGroupHeader
                    role={g.role}
                    count={g.members.length}
                    open={open}
                    onAltClick={closeAllGroups}
                  />

                  <CollapsibleContent className="flex flex-col gap-0.5 px-2 pt-0.5">
                    {g.members.map((u: any) => {
                      const stats = userStatsMap.get(u.id) ?? {
                        resourceCount: 0,
                        pointsTotal: 0,
                        scheduledHours: 0,
                        taskCount: 0,
                      };
                      return (
                        <UserCard
                          key={u.id}
                          user={u}
                          active={u.id === selectedUser?.id}
                          onSelect={() => selectUser(u.id)}
                          resourceCount={stats.resourceCount}
                          pointsTotal={stats.pointsTotal}
                          scheduledHours={stats.scheduledHours}
                          taskCount={stats.taskCount}
                        />
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
